import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './Topbar.css';
import Cookies from 'js-cookie';
import { FaBell, FaUser, FaArrowDown, FaSignOutAlt, FaCog } from 'react-icons/fa';
import { MdMenu } from 'react-icons/md';
import axios from 'axios';
import API_BASE_URL from '../../../config/api'

const AnimatedGreeting = () => {
    const [messageIndex, setMessageIndex] = useState(0);
    const [fadeState, setFadeState] = useState('fade-in');
    const [profile, setProfile] = useState([])

    
    const getTimeBasedGreeting = () => {
        const hour = new Date().getHours();
        if (hour >= 1 && hour < 12) return "Good Morning";
        if (hour >= 12 && hour < 17) return "Good Afternoon";
        return "Good Evening";
    };
    
    const greetings = [
        `${getTimeBasedGreeting()}, ${profile.first_name} ✨`,
    ];

    useEffect(() => {
        // Update the first greeting every minute to keep time-based greeting current
        const timeCheckInterval = setInterval(() => {
            greetings[0] = `${getTimeBasedGreeting()}, hope you are having a great day! ✨`;
        }, 120000);

        // Message rotation interval
        const messageInterval = setInterval(() => {
            setFadeState('fade-out');
            
            setTimeout(() => {
                setMessageIndex((prev) => (prev + 1) % greetings.length);
                setFadeState('fade-in');
            }, 500); // Half of the total animation duration
            
        }, 6000); // Changed from 4000 to 6000 - messages stay for 6 seconds

        return () => {
            clearInterval(messageInterval);
            clearInterval(timeCheckInterval);
        };
    }, []);

    return (
        <div className={`animated-greeting ${fadeState}`}>
            {messageIndex === 0 ? getTimeBasedGreeting() + greetings[0].substring(getTimeBasedGreeting().length) : greetings[messageIndex]}
        </div>
    );
};

const Topbar = () => {
    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [showNotifications, setShowNotifications] = useState(false);
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const profileRef = useRef(null);
    const notificationRef = useRef(null);
    const navigate = useNavigate();
    const baseUrl = API_BASE_URL

    useEffect(() => {
        // Fetch profile data from /api/users/me
        const fetchProfile = async () => {
            setLoading(true);
            setError(null);
            try {
                const token = Cookies.get('token');
                const response = await axios.get(`${baseUrl}/api/users/me`, {
                    headers: {
                        'Content-Type': 'application/json',
                        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                    },
                });
                console.log(response.data)
                setProfile(response.data);
            } catch (err) {
                setError(err.response?.data?.message || err.message || 'Failed to fetch profile');
            } finally {
                setLoading(false);
            }
        };
        fetchProfile();
    }, []);

    useEffect(() => {
        // Close dropdowns when clicking outside
        const handleClickOutside = (event) => {
            if (profileRef.current && !profileRef.current.contains(event.target)) {
                setIsProfileOpen(false);
            }
            if (notificationRef.current && !notificationRef.current.contains(event.target)) {
                setShowNotifications(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleLogout = () => {
        Cookies.remove('token');
        navigate('/login');
    };

    const handleSidebarToggle = () => {
        window.dispatchEvent(new Event('toggle-sidebar'));
    };

    useEffect(() => {
        const syncMobileLayout = () => {
            document.body.classList.toggle('employee-sidebar-mobile', window.innerWidth <= 768);
        };

        syncMobileLayout();
        window.addEventListener('resize', syncMobileLayout);
        return () => {
            window.removeEventListener('resize', syncMobileLayout);
            document.body.classList.remove('employee-sidebar-mobile');
        };
    }, []);

    return (
        <div className="topbar employee-topbar">
            <div className="topbar-left">
                <button
                    type="button"
                    className="employee-hamburger-btn"
                    onClick={handleSidebarToggle}
                    aria-label="Toggle sidebar menu"
                >
                    <MdMenu aria-hidden="true" />
                </button>
            </div>

            <div className="topbar-right">
                {/* Notifications */}
                <div className="notification-container" ref={notificationRef}>
                    <button 
                        className="notification-btn"
                        onClick={() => setShowNotifications(!showNotifications)}
                    >
                        <span className="notification-icon"><FaBell /></span>
                        {notifications.length > 0 && (
                            <span className="notification-badge">{notifications.length}</span>
                        )}
                    </button>

                    {showNotifications && (
                        <div className="notification-dropdown">
                            <div className="notification-header">
                                <h3>Notifications</h3>
                                <button className="clear-btn">Clear All</button>
                            </div>
                            {notifications.length === 0 ? (
                                <div className="no-notifications">
                                    No new notifications
                                </div>
                            ) : (
                                <div className="notification-list">
                                    {notifications.map((notification, index) => (
                                        <div key={index} className="notification-item">
                                            {notification.message}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Profile */}
                <div className="profile-container" ref={profileRef}>
                    <button 
                        className="profile-btn"
                        onClick={() => setIsProfileOpen(!isProfileOpen)}
                        disabled={loading}
                    >
                        <span className="profile-avatar"><FaUser /></span>
                        <span className="profile-name">
                            {loading ? 'Loading...' : error ? 'Error' : profile ? profile?.employee?.first_name : 'Unknown'}
                        </span>
                        <span className={`dropdown-arrow ${isProfileOpen ? 'open' : ''}`}><FaArrowDown /></span>
                    </button>

                    {isProfileOpen && !loading && profile && (
                        <div className="profile-dropdown">
                            <div className="profile-header">
                                <div className="profile-info">
                                    <p>{profile?.employee?.user?.email}</p>
                                    <span className="role-badge">{profile?.employee?.position}</span>
                                </div>
                            </div>
                            
                            <div className="profile-menu">
                               
                                <Link to="/employee/change-password" className="profile-menu-item" onClick={() => setIsProfileOpen(false)}>
                                    <span className="topbar-profile-menu-icon"><FaCog /></span>
                                    Change Password
                                </Link>
                                <button className="profile-menu-item logout" onClick={handleLogout}>
                                    <span className="topbar-profile-menu-icon"><FaSignOutAlt /></span>
                                    Logout
                                </button>
                            </div>
                        </div>
                    )}
                    {isProfileOpen && error && (
                        <div className="profile-dropdown">
                            <div className="profile-header">
                                <div className="profile-info">
                                    <p>Error loading profile</p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Topbar;
